import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Dealer Signup Service
 * Handles all API calls related to public dealer sign-up flow
 */
const DealerSignupService = {
  /**
   * Validate installer referral code (ID or email)
   * @param {string|number} code - Installer ID or email
   * @returns {Promise} - { valid: boolean, installerId: number, installerName: string }
   */
  validateInstallerReferral: async (code) => {
    const isNumeric = /^\d+$/.test(code);
    const endpoint = isNumeric
      ? `/public/validate-installer/${code}`
      : `/public/validate-installer-email/${code}`;

    const response = await axios.get(`${API_URL}${endpoint}`);
    return response.data;
  },

  /**
   * Check if email already exists in system
   * @param {string} email
   * @returns {Promise} - { exists: boolean, message: string }
   */
  checkEmailExists: async (email) => {
    const response = await axios.get(`${API_URL}/public/dealer-signup/validate-email`, {
      params: { email }
    });
    return response.data;
  },

  /**
   * Submit complete dealer signup
   * Creates dealer, user, Cognito account, and triggers proximity calculations
   * @param {Object} signupData
   * @returns {Promise} - { success: boolean, dealerId: number, userId: number, message: string }
   */
  submitDealerSignup: async (signupData) => {
    const response = await axios.post(`${API_URL}/public/dealer-signup`, {
      // Dealer information (matches POST /dealers structure)
      dealerName: signupData.businessName,
      dealerEmail: signupData.contactEmail,
      dealerPhoneNumber: signupData.contactPhone,
      dealerAddressLine1: signupData.addressLine1,
      dealerAddressLine2: signupData.addressLine2 || '',
      dealerAddressCity: signupData.city,
      dealerAddressState: signupData.state,
      dealerAddressZip: signupData.zipCode,
      dealerActive: 1,
      customerContract: 'N',

      // Service configuration
      // If installer referral: use their categories, otherwise default to "Paint"
      installerCategories: signupData.installerCategories || 'Paint',

      // User information (matches POST /users structure)
      userName: signupData.contactName,
      userEmail: signupData.contactEmail,
      userPhoneNumber: signupData.contactPhone,
      userType: 'dealer',
      access: 'STANDARD',
      active: 1,

      // Attribution and referral tracking
      attributionSource: signupData.attributionSource, // 'direct', 'internal', 'installer'
      referringInstallerId: signupData.referringInstallerId || null,
      referralCode: signupData.referralCode || null,

      // Flag to mark preferred installer after proximity calculation
      markPreferredInstaller: signupData.referringInstallerId ? true : false
    });

    return response.data;
  },

  /**
   * Get installer categories for a given installer ID
   * Used to determine which services to configure when signing up via installer referral
   * @param {number} installerId
   * @returns {Promise} - { installerCategories: string }
   */
  getInstallerCategories: async (installerId) => {
    const response = await axios.get(`${API_URL}/public/installer-categories/${installerId}`);
    return response.data;
  }
};

export default DealerSignupService;
